/**
 * @internal Do not import from outside SellPriceUI folder.
 */
import { useCallback, useMemo } from 'react';
import type { SellPriceLabels } from '#types/System.types';
import type {
  FormulaTypeId,
  FormulaTypeConfig,
  CaretDirection,
  ResolvedSellPriceLabels,
  SellPriceCellPayload,
} from './types';

import { DEFAULT_FORMULA_TYPE, FORMULA_TYPES } from './config';

const EPSILON = 1e-9;
const DISPLAY_RATE_EPSILON = 0.005 + EPSILON;

function isFormulaTypeId(value: unknown): value is FormulaTypeId {
  return typeof value === 'string' && value in FORMULA_TYPES;
}

function getFormulaType(formulaTypeId: string | undefined): FormulaTypeConfig {
  return formulaTypeId && isFormulaTypeId(formulaTypeId)
    ? FORMULA_TYPES[formulaTypeId]
    : DEFAULT_FORMULA_TYPE;
}

function buildPayload(
  basePrice: number,
  ratePercent: number,
  formulaTypeId: string,
  sellPrice: number,
): SellPriceCellPayload {
  return {
    fields: {
      basePrice: { value: basePrice },
      ratePercent: { value: ratePercent },
      formulaTypeId: { value: formulaTypeId },
      sellPrice: { value: sellPrice },
    },
  };
}

function resolveCaretDirection(config: FormulaTypeConfig, ratePercent: number): CaretDirection {
  if (ratePercent === 0) return 'neutral';
  if (ratePercent < 0) {
    return config.defaultCaretDirection === 'up' ? 'down' : 'up';
  }
  return config.defaultCaretDirection;
}

type UseSellPriceUIProps = {
  basePrice: number;
  ratePercent: number;
  formulaTypeId: string;
  sellPrice: number;
  sellPriceLabels?: SellPriceLabels;
  onChange?: (payload: SellPriceCellPayload) => void;
};

export const useSellPriceUI = ({
  basePrice,
  ratePercent,
  formulaTypeId: rawFormulaTypeId,
  sellPrice,
  sellPriceLabels,
  onChange = () => {},
}: UseSellPriceUIProps) => {
  const config = getFormulaType(rawFormulaTypeId);
  const formulaTypeId = config.formulaTypeId;
  const calcOutput = typeof sellPrice === 'number' && Number.isFinite(sellPrice) ? sellPrice : 0;

  const caretDirection = useMemo(
    () => resolveCaretDirection(config, ratePercent),
    [config, ratePercent],
  );

  const labels = useMemo((): ResolvedSellPriceLabels => ({
    basePriceLabel: sellPriceLabels?.basePriceLabel || config.basePriceLabel,
    sellPriceLabel: sellPriceLabels?.sellPriceLabel || config.sellPriceLabel,
    ratePercentLabel: sellPriceLabels?.ratePercentLabel || config.ratePercentLabel,
    formulaDisplay: config.formulaDisplay,
  }), [sellPriceLabels, config]);

  const handleClear = useCallback(() => {
    const newSell = config.forward(basePrice, 0);
    onChange(buildPayload(basePrice, 0, formulaTypeId, newSell));
  }, [onChange, basePrice, formulaTypeId, config]);

  const handleOutputChange = useCallback(
    (newOutput: number | null) => {
      const out = newOutput ?? 0;
      if (!Number.isFinite(out) || !Number.isFinite(basePrice) || basePrice === 0) {
        onChange(buildPayload(basePrice, ratePercent, formulaTypeId, out));
        return;
      }
      const newRate = config.reverseRate(out, basePrice);
      const nextRate = Math.abs(newRate - ratePercent) <= EPSILON ? ratePercent : newRate;
      onChange(buildPayload(basePrice, nextRate, formulaTypeId, out));
    },
    [onChange, basePrice, ratePercent, formulaTypeId, config],
  );

  const handleRatePercentChange = useCallback(
    (newRatePercent: number | null) => {
      const rate = newRatePercent ?? 0;
      const nextRate = newRatePercent !== null && Math.abs(rate - ratePercent) <= DISPLAY_RATE_EPSILON
        ? ratePercent
        : rate;
      const newSell = config.forward(basePrice, nextRate);
      onChange(buildPayload(basePrice, nextRate, formulaTypeId, newSell));
    },
    [onChange, basePrice, ratePercent, formulaTypeId, config],
  );

  return {
    calcOutput,
    basePrice,
    ratePercent,
    caretDirection,
    labels,
    handleClear,
    handleOutputChange,
    handleRatePercentChange,
  };
};
